import React from "react";
import { useSelector } from "react-redux";
import GenericModal from "../CommonComponents/genericModal/GenericModal";
import { useFetchOrderDetailQuery } from "../../Apis/ordersApi";
import { useFetchCompanyInfosQuery } from "../../Apis/companyApi";
import { getOrderStatusDisplayText, isOrderAcceptedOrLater } from "../../Enums/orderStatus";
import { parseDate, formatDate, DATE_FORMATS } from "../../Util/dateUtils";
import LoadingSpinner from "../CommonComponents/loadingSpinner";
import PodViewer from "./PodViewer";
import styles from "./OrderDetailModal.module.css";

const formatDateTime = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    const date = parseDate(timestamp);
    return formatDate(date, DATE_FORMATS.MMM_DDc_YYYY_HHcMM_AP);
  } catch (e) {
    return "N/A";
  }
};

const formatTimeOnly = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    const date = parseDate(timestamp);
    return formatDate(date, DATE_FORMATS.HHcMM_AP);
  } catch (e) {
    return "N/A";
  }
};

const formatCurrency = (value) => {
  if (value === 0 || value === null || value === undefined) return "N/A";
  return `$${parseFloat(value).toFixed(2)}`;
};

const OrderDetailModal = ({ orderId, companyId, open, onClose }) => {
  const { data: partnerAccounts } = useFetchCompanyInfosQuery();
  const companyName = partnerAccounts?.getAccountDetailsByCompanyId(companyId)?.companyName ?? "";
  const { data: orderDetail, isFetching, error } = useFetchOrderDetailQuery(
    { orderId, companyId },
    { skip: !open || !orderId || !companyId }
  );

  return (
    <GenericModal 
      open={open} 
      onClose={onClose}
      backdropClassName={styles.modalBackdrop}
      modalClassName={styles.modalDialog}
    >
      <div className={styles.modalContent}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.modalTitle}>
              Order #: {orderDetail?.getOrderNumber() || orderId}
            </div>
            {companyName && (
              <div className={styles.companyName}>{companyName}</div>
            )}
            {orderDetail && (
              <div className={styles.modalStatus}>
                Status: {getOrderStatusDisplayText(orderDetail.getOrderStatus())} {" "}
                {orderDetail.getTrackingLink() && 
                 isOrderAcceptedOrLater(orderDetail.getOrderStatus()) && (
                  <a
                    href={orderDetail.getTrackingLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.trackLink}
                  >
                    Track order
                  </a>
                )}
              </div>
            )}
          </div>
          <div className={styles.headerRight}>
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={styles.scrollableContent}>
          {isFetching && (
            <div className={styles.loadingState}>
              <LoadingSpinner />
            </div>
          )}

          {!isFetching && error && (
            <div className={styles.errorState}>
              Error loading order details: {error?.data?.errorMessage || error?.data?.message || error?.message || `HTTP ${error?.status}` || "Unknown error"}
            </div>
          )}

          {!isFetching && !error && orderDetail && (
            <>
              {/* Delivery and Pickup Section */}
              <div className={styles.section}>
                <div className={styles.twoColumn}>
                  <div className={styles.column}>
                    <div className={styles.sectionTitle}>Deliver to</div>
                    {orderDetail.getCustomer() && (
                      <>
                        <div className={styles.boldText}>
                          {orderDetail.getCustomer().getName()}
                        </div>
                        <div className={styles.text}>
                          {orderDetail.getCustomer().getAddress()}
                        </div>
                        {orderDetail.getCustomer().getPhoneNumber() && (
                          <div className={styles.text}>
                            {orderDetail.getCustomer().getPhoneNumber()}
                          </div>
                        )}
                        {orderDetail.getCustomer().getEmails()?.length > 0 && (
                          <div className={styles.text}>
                            {orderDetail.getCustomer().getEmails().join(", ")}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className={styles.column}>
                    <div className={styles.sectionTitle}>Pick-up From</div>
                    {orderDetail.getRestaurant() && (
                      <>
                        <div className={styles.boldText}>
                          {orderDetail.getRestaurant().getName()}
                        </div>
                        <div className={styles.text}>
                          {orderDetail.getRestaurant().getAddress()}
                        </div>
                        {orderDetail.getRestaurant().getPhoneNumber() && (
                          <div className={styles.text}>
                            {orderDetail.getRestaurant().getPhoneNumber()}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Section */}
              <div className={styles.section}>
                <div className={styles.sectionTitle}>Order</div>
                {orderDetail.getOrderItems()?.length > 0 ? (
                  <div className={styles.orderItems}>
                    {orderDetail.getOrderItems().map((item, index) => (
                      <div key={index} className={styles.orderItem}>
                        <div className={styles.itemHeader}>
                          <span className={styles.itemName}>
                            {item.getQuantity()} x {item.getName()}
                          </span>
                          <span className={styles.itemPrice}>
                            {formatCurrency(
                              parseFloat(item.getUnitPrice()) * item.getQuantity()
                            )}
                          </span>
                        </div>
                        {item.getAddOns()?.length > 0 && (
                          <div className={styles.itemAddOns}>
                            {item.getAddOns().map((addOn, idx) => (
                              <div key={idx} className={styles.addOn}>
                                +{addOn}
                              </div>
                            ))}
                          </div>
                        )}
                        {item.getDetail() && (
                          <div className={styles.itemDetail}>{item.getDetail()}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.text}>No items</div>
                )}
                {orderDetail.getCostInfo() && (
                  <div className={styles.costSummary}>
                    <div className={styles.costRow}>
                      <span>Tax</span>
                      <span>{formatCurrency(orderDetail.getCostInfo().getTax())}</span>
                    </div>
                    <div className={styles.costRow}>
                      <span>Delivery Fees</span>
                      <span>
                        {formatCurrency(orderDetail.getCostInfo().getDeliveryFee())}
                      </span>
                    </div>
                    <div className={styles.costRow}>
                      <span>Delivery Tips</span>
                      <span>{formatCurrency(orderDetail.getCostInfo().getTip())}</span>
                    </div>
                    <div className={styles.costRow}>
                      <span>Discount</span>
                      <span>
                        {formatCurrency(orderDetail.getCostInfo().getDiscountAmount())}
                      </span>
                    </div>
                    <div className={`${styles.costRow} ${styles.totalRow}`}>
                      <span>Total</span>
                      <span>
                        {formatCurrency(orderDetail.getCostInfo().getTotalCost())}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Delivery Details Section */}
              <div className={styles.section}>
                <div className={styles.sectionTitle}>Delivery Details</div>
                {orderDetail.getActivityLog() && (
                  <div className={styles.deliveryDetails}>
                    <div className={styles.detailRow}>
                      <span>Order Placement Time:</span>
                      <span>
                        {formatDateTime(orderDetail.getActivityLog().getPlacementTime())}
                      </span>
                    </div>
                    {orderDetail.getAssignedCarrier() && (
                      <div className={styles.detailRow}>
                        <span>Driver:</span>
                        <span>{orderDetail.getAssignedCarrier().getName()}</span>
                      </div>
                    )}
                    <div className={styles.detailRow}>
                      <span>Requested Pickup Time:</span>
                      <span>
                        {orderDetail.getActivityLog().getExpectedPickupTime()
                          ? formatTimeOnly(
                              orderDetail.getActivityLog().getExpectedPickupTime()
                            )
                          : "N/A"}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span>Requested Delivery Time:</span>
                      <span>
                        {formatDateTime(
                          `${orderDetail.getActivityLog().getExpectedDeliveryDate()}T${orderDetail.getActivityLog().getExpectedDeliveryTime()}Z`
                        )}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span>Order Accept Time:</span>
                      <span>
                        {formatDateTime(orderDetail.getActivityLog().getAssignedTime())}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span>Order Pickup Time:</span>
                      <span>
                        {formatDateTime(orderDetail.getActivityLog().getPickedUpTime())}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span>Order Delivery Time:</span>
                      <span>
                        {formatDateTime(orderDetail.getActivityLog().getDeliveryTime())}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span>Order Completion Time:</span>
                      <span>
                        {formatDateTime(
                          orderDetail.getActivityLog().getFailedDeliveryTime()
                        )}
                      </span>
                    </div>
                    {orderDetail.getDeliveryInstruction() && (
                      <div className={styles.detailRow}>
                        <span>Delivery Instruction:</span>
                        <span>{orderDetail.getDeliveryInstruction()}</span>
                      </div>
                    )}
                    {!orderDetail.getAssignedCarrier() && (
                      <div className={styles.detailRow}>
                        <span>Driver:</span>
                        <span>Not assigned</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Payment Details Section */}
              <div className={styles.section}>
                <div className={styles.sectionTitle}>Payment Details</div>
                <div className={styles.paymentDetails}>
                    <div className={styles.detailRow}>
                      <span>Payment Method:</span>
                      <span>
                        {orderDetail.getPaymentInfo().getPaymentMethod() || "N/A"}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span>Proof of Delivery:</span>
                      {(() => {
                        const pod = orderDetail.getProofOfDelivery();
                        const images = pod?.getImageUrls() ?? [];
                        const signature = pod?.getSignaturePath();
                        return images.length > 0 || signature ? (
                          <PodViewer images={images} signature={signature} />
                        ) : (
                          <span>N/A</span>
                        );
                      })()}
                    </div>
                  <div className={styles.detailRow}>
                    <span>Delivery Note:</span>
                    <span>{orderDetail.getDeliveryNote() || "N/A"}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </GenericModal>
  );
};

export default OrderDetailModal;
